import { useEffect, useState } from "react";

import {
    Box,
    Container,
    Typography,
    Paper,
    Button,
    Stack,
    IconButton,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    CircularProgress,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";

import { useDocumentos } from "../../hooks/UseDocumentos";
import StatusModal from "../../utils/StatusModal";


export const Documentos = () => {

    const {
        documentos,
        loading,
        error,
        cargarDocumentos,
        guardarDocumento,
        borrarDocumento,
    } = useDocumentos();

    // =====================================================
    // ESTADOS
    // =====================================================

    const [openModal, setOpenModal] = useState(false);

    const [tipoFiltro, setTipoFiltro] = useState("todos");

    const [formulario, setFormulario] = useState({
        titulo: "",
        tipo: "",
        descripcion: "",
        archivo: null,
    });

    const [statusModal, setStatusModal] = useState({
        open: false,
        type: "success",
        message: "",
    });


    // =====================================================
    // CARGAR DOCUMENTOS
    // =====================================================

    useEffect(() => {

        cargarDocumentos();

    }, []);


    // =====================================================
    // STATUS
    // =====================================================

    const mostrarStatus = (type, message) => {

        setStatusModal({
            open: true,
            type,
            message,
        });

    };


    const cerrarStatus = () => {

        setStatusModal((prev) => ({
            ...prev,
            open: false,
        }));

    };



    const handleNuevo = () => {

        setFormulario({
            titulo: "",
            tipo: "",
            descripcion: "",
            archivo: null,
        });

        setOpenModal(true);

    };


    const cerrarFormulario = () => {

        if (loading) return;

        setOpenModal(false);

        setFormulario({
            titulo: "",
            tipo: "",
            descripcion: "",
            archivo: null,
        });

    };


    const handleChange = (event) => {

        const {
            name,
            value,
        } = event.target;

        setFormulario((prev) => ({
            ...prev,
            [name]: value,
        }));

    };


    const handleArchivo = (event) => {

        const archivo = event.target.files?.[0];

        if (!archivo) return;


        if (
            archivo.type !== "application/pdf" &&
            !archivo.name.toLowerCase().endsWith(".pdf")
        ) {

            mostrarStatus(
                "error",
                "Solo puedes seleccionar archivos PDF."
            );

            event.target.value = "";

            return;
        }


        setFormulario((prev) => ({
            ...prev,
            archivo,
        }));

    };


    const handleGuardar = async () => {
          mostrarStatus({
        status: "loading",
        title: "Creando documento",
        message: "Estamos guardando el documento. Por favor espera...",
    });

        // -----------------------------------------------
        // TITULO
        // -----------------------------------------------

        if (!formulario.titulo.trim()) {

            mostrarStatus(
                "error",
                "Debes ingresar el título del documento."
            );

            return;
        }


        // -----------------------------------------------
        // TIPO
        // -----------------------------------------------

        if (!formulario.tipo) {

            mostrarStatus(
                "error",
                "Debes seleccionar el tipo de documento."
            );

            return;
        }


        // -----------------------------------------------
        // ARCHIVO
        // -----------------------------------------------

        if (!formulario.archivo) {

            mostrarStatus(
                "error",
                "Debes seleccionar un archivo PDF."
            );

            return;
        }


        try {

            const formData = new FormData();

            formData.append(
                "titulo",
                formulario.titulo.trim()
            );

            formData.append(
                "tipo",
                formulario.tipo
            );

            formData.append(
                "descripcion",
                formulario.descripcion.trim()
            );

            formData.append(
                "archivo",
                formulario.archivo
            );


            await guardarDocumento(formData);


            cerrarFormulario();
 

             mostrarStatus(
                "success",
                "El documento se creó correctamente."
            );
        

        setTimeout(() => {
            cerrarStatus();

        }, 1800);

        } catch (error) {

            console.error(
                "Error al crear documento:",
                error
            );

            mostrarStatus(
                "error",
                error?.response?.data?.message ||
                "No fue posible crear el documento."
            );


        setTimeout(() => {
            cerrarStatus();

        }, 1800);

        }

    };


    // =====================================================
    // ELIMINAR
    // =====================================================

    const handleEliminar = async (documento) => {

        const confirmar = window.confirm(
            `¿Estás seguro de eliminar "${documento.titulo}"?`
        );

        if (!confirmar) return;


        try {

            await borrarDocumento(documento.id);

            mostrarStatus(
                "success",
                "El documento se eliminó correctamente."
            );

            setTimeout(() => {
            cerrarStatus();

        }, 1800);

        } catch (error) {

            console.error(error);

            mostrarStatus(
                "error",
                error?.response?.data?.message ||
                "No fue posible eliminar el documento."
            );

            setTimeout(() => {
            cerrarStatus();

        }, 1800);

        }

    };


    // =====================================================
    // VER PDF
    // =====================================================

    const handleVerPDF = (documento) => {

        if (!documento.archivo) {

            mostrarStatus(
                "error",
                "No se encontró el archivo PDF."
            );

            return;
        }


        const backendURL =
            process.env.REACT_APP_BACKEND_URL;


        const url =
            `${backendURL}/${documento.archivo}`;


        window.open(
            url,
            "_blank"
        );

    };


    // =====================================================
    // FILTRAR
    // =====================================================

    const documentosFiltrados =
        tipoFiltro === "todos"
            ? documentos
            : documentos.filter(
                (documento) =>
                    documento.tipo === tipoFiltro
            );


    // =====================================================
    // TIPO
    // =====================================================

    const obtenerColorTipo = (tipo) => {

        switch (tipo) {

            case "reglamento":
                return {
                    background: "#FEE2E2",
                    color: "#991B1B",
                };

            case "manual":
                return {
                    background: "#DBEAFE",
                    color: "#1D4ED8",
                };

            case "protocolo":
                return {
                    background: "#DCFCE7",
                    color: "#15803D",
                };

            case "institucional":
                return {
                    background: "#F3E8FF",
                    color: "#7E22CE",
                };

            default:
                return {
                    background: "#F1F5F9",
                    color: "#475569",
                };
        }

    };


    // =====================================================
    // RENDER
    // =====================================================

    return (

        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#F7F8FC",
                py: 4,
            }}
        >

            <Container maxWidth="xl">

                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: {
                            xs: "flex-start",
                            md: "center",
                        },
                        flexDirection: {
                            xs: "column",
                            md: "row",
                        },
                        gap: 2,
                        mb: 4,
                    }}
                >

                    <Box>

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                color: "#1E293B",
                                mb: 0.5,
                            }}
                        >
                            Documentos
                        </Typography>

                        <Typography
                            sx={{
                                color: "#64748B",
                                fontSize: "0.95rem",
                            }}
                        >
                            Administra los documentos institucionales
                            del establecimiento.
                        </Typography>

                    </Box>


                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleNuevo}
                        sx={{
                            backgroundColor: "#3157A4",
                            borderRadius: "11px",
                            px: 2.5,
                            py: 1.2,
                            textTransform: "none",
                            fontWeight: 700,
                            boxShadow:
                                "0 6px 16px rgba(49,87,164,0.20)",

                            "&:hover": {
                                backgroundColor: "#264889",
                            },
                        }}
                    >
                        Agregar documento
                    </Button>

                </Box>


                {/* ================================================= */}
                {/* TABLA */}
                {/* ================================================= */}

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: "18px",
                        border: "1px solid #E5E7EB",
                        backgroundColor: "#FFFFFF",
                        overflow: "hidden",
                    }}
                >

                    {/* CABECERA */}

                    <Box
                        sx={{
                            px: 3,
                            py: 2.5,
                        }}
                    >

                        <Stack
                            direction={{
                                xs: "column",
                                md: "row",
                            }}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "flex-start",
                                md: "center",
                            }}
                            spacing={2}
                        >

                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                            >

                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: "11px",
                                        backgroundColor: "#EEF3FF",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >

                                    <DescriptionRoundedIcon
                                        sx={{
                                            color: "#3157A4",
                                            fontSize: 23,
                                        }}
                                    />

                                </Box>


                                <Box>

                                    <Typography
                                        sx={{
                                            fontWeight: 750,
                                            color: "#1E293B",
                                        }}
                                    >
                                        Documentos publicados
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "0.78rem",
                                            color: "#94A3B8",
                                        }}
                                    >
                                        Reglamentos, manuales,
                                        protocolos e información
                                        institucional.
                                    </Typography>

                                </Box>

                            </Stack>


                            {/* FILTRO */}

                            <TextField
                                select
                                size="small"
                                value={tipoFiltro}
                                onChange={(event) =>
                                    setTipoFiltro(
                                        event.target.value
                                    )
                                }
                                sx={{
                                    minWidth: 180,

                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                    },
                                }}
                            >

                                <MenuItem value="todos">
                                    Todos los documentos
                                </MenuItem>

                                <MenuItem value="reglamento">
                                    Reglamentos
                                </MenuItem>

                                <MenuItem value="manual">
                                    Manuales
                                </MenuItem>

                                <MenuItem value="protocolo">
                                    Protocolos
                                </MenuItem>

                                <MenuItem value="institucional">
                                    Institucional
                                </MenuItem>

                            </TextField>

                        </Stack>

                    </Box>


                    <Divider />


                    {/* ================================================= */}
                    {/* TABLA */}
                    {/* ================================================= */}

                    <Box
                        sx={{
                            overflowX: "auto",
                        }}
                    >

                        <Box
                            sx={{
                                minWidth: 850,
                            }}
                        >

                            {/* HEADER */}

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "1.2fr 2fr 3fr 150px",
                                    px: 3,
                                    py: 1.8,
                                    backgroundColor: "#F8FAFC",
                                    borderBottom:
                                        "1px solid #E5E7EB",
                                }}
                            >

                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 800,
                                        color: "#64748B",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Tipo
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 800,
                                        color: "#64748B",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Documento
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 800,
                                        color: "#64748B",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Descripción
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 800,
                                        color: "#64748B",
                                        textTransform: "uppercase",
                                        textAlign: "center",
                                    }}
                                >
                                    Acciones
                                </Typography>

                            </Box>


                            {/* LOADING */}

                            {loading && !documentos.length && (

                                <Box
                                    sx={{
                                        py: 8,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >

                                    <CircularProgress
                                        size={30}
                                        sx={{
                                            color: "#3157A4",
                                        }}
                                    />

                                </Box>

                            )}


                            {/* FILAS */}

                            {!loading &&
                                documentosFiltrados.map(
                                    (documento) => {

                                        const color =
                                            obtenerColorTipo(
                                                documento.tipo
                                            );

                                        return (

                                            <Box
                                                key={documento.id}
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "1.2fr 2fr 3fr 150px",
                                                    alignItems:
                                                        "center",
                                                    px: 3,
                                                    py: 2,
                                                    borderBottom:
                                                        "1px solid #EEF1F5",

                                                    "&:hover": {
                                                        backgroundColor:
                                                            "#FAFBFD",
                                                    },
                                                }}
                                            >

                                                {/* TIPO */}

                                                <Box>

                                                    <Chip
                                                        label={
                                                            documento.tipo
                                                                ?.charAt(0)
                                                                .toUpperCase() +
                                                            documento.tipo?.slice(
                                                                1
                                                            )
                                                        }
                                                        size="small"
                                                        sx={{
                                                            backgroundColor:
                                                                color.background,
                                                            color:
                                                                color.color,
                                                            fontWeight: 700,
                                                            borderRadius:
                                                                "8px",
                                                            fontSize:
                                                                "0.72rem",
                                                        }}
                                                    />

                                                </Box>


                                                {/* DOCUMENTO */}

                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1.2}
                                                >

                                                    <Box
                                                        sx={{
                                                            width: 38,
                                                            height: 38,
                                                            borderRadius:
                                                                "9px",
                                                            backgroundColor:
                                                                "#FEF2F2",
                                                            display:
                                                                "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            flexShrink: 0,
                                                        }}
                                                    >

                                                        <PictureAsPdfRoundedIcon
                                                            sx={{
                                                                color:
                                                                    "#DC2626",
                                                                fontSize: 21,
                                                            }}
                                                        />

                                                    </Box>


                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                "0.85rem",
                                                            color:
                                                                "#334155",
                                                            fontWeight: 700,
                                                            overflow:
                                                                "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {
                                                            documento.titulo
                                                        }
                                                    </Typography>

                                                </Stack>


                                                {/* DESCRIPCIÓN */}

                                                <Typography
                                                    sx={{
                                                        fontSize:
                                                            "0.82rem",
                                                        color:
                                                            "#64748B",
                                                        overflow:
                                                            "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace:
                                                            "nowrap",
                                                        pr: 2,
                                                    }}
                                                >
                                                    {
                                                        documento.descripcion ||
                                                        "Sin descripción"
                                                    }
                                                </Typography>


                                                {/* ACCIONES */}

                                                <Stack
                                                    direction="row"
                                                    justifyContent="center"
                                                    spacing={0.5}
                                                >

                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleVerPDF(
                                                                documento
                                                            )
                                                        }
                                                        sx={{
                                                            color:
                                                                "#3157A4",

                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "#EEF3FF",
                                                            },
                                                        }}
                                                    >

                                                        <VisibilityRoundedIcon
                                                            fontSize="small"
                                                        />

                                                    </IconButton>


                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleEliminar(
                                                                documento
                                                            )
                                                        }
                                                        sx={{
                                                            color:
                                                                "#DC2626",

                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "#FEF2F2",
                                                            },
                                                        }}
                                                    >

                                                        <DeleteOutlineRoundedIcon
                                                            fontSize="small"
                                                        />

                                                    </IconButton>

                                                </Stack>

                                            </Box>

                                        );

                                    }
                                )}


                            {/* SIN RESULTADOS */}

                            {!loading &&
                                !documentosFiltrados.length && (

                                    <Box
                                        sx={{
                                            py: 8,
                                            textAlign: "center",
                                        }}
                                    >

                                        <DescriptionRoundedIcon
                                            sx={{
                                                fontSize: 45,
                                                color: "#CBD5E1",
                                                mb: 1,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: "#475569",
                                            }}
                                        >
                                            No hay documentos
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "0.85rem",
                                                color: "#94A3B8",
                                                mt: 0.5,
                                            }}
                                        >
                                            No existen documentos
                                            para el filtro seleccionado.
                                        </Typography>

                                    </Box>

                                )}

                        </Box>

                    </Box>

                </Paper>

            </Container>


            {/* ===================================================== */}
            {/* MODAL CREAR */}
            {/* ===================================================== */}

            <Dialog
                open={openModal}
                onClose={cerrarFormulario}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "18px",
                    },
                }}
            >

                <DialogTitle
                    sx={{
                        px: 3,
                        pt: 3,
                        pb: 1,
                    }}
                >

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >

                        <Box>

                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "1.25rem",
                                }}
                            >
                                Agregar documento
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "0.8rem",
                                    color: "#94A3B8",
                                    mt: 0.3,
                                }}
                            >
                                Completa la información del documento.
                            </Typography>

                        </Box>


                        <IconButton
                            onClick={cerrarFormulario}
                            disabled={loading}
                        >
                            <CloseRoundedIcon />
                        </IconButton>

                    </Stack>

                </DialogTitle>


                <DialogContent
                    sx={{
                        px: 3,
                        pt: 2,
                    }}
                >

                    <Stack spacing={2.5}>

                        {/* TITULO */}

                        <TextField
                            fullWidth
                            label="Título"
                            name="titulo"
                            value={formulario.titulo}
                            onChange={handleChange}
                            disabled={loading}
                            placeholder="Ej: Reglamento Interno"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "11px",
                                },
                            }}
                        />


                        {/* TIPO */}

                        <TextField
                            select
                            fullWidth
                            label="Tipo de documento"
                            name="tipo"
                            value={formulario.tipo}
                            onChange={handleChange}
                            disabled={loading}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "11px",
                                },
                            }}
                        >

                            <MenuItem value="reglamento">
                                Reglamento
                            </MenuItem>

                            <MenuItem value="manual">
                                Manual
                            </MenuItem>

                            <MenuItem value="protocolo">
                                Protocolo
                            </MenuItem>

                            <MenuItem value="institucional">
                                Institucional
                            </MenuItem>

                        </TextField>


                        {/* DESCRIPCIÓN */}

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Descripción"
                            name="descripcion"
                            value={formulario.descripcion}
                            onChange={handleChange}
                            disabled={loading}
                            placeholder="Describe brevemente el documento..."
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "11px",
                                },
                            }}
                        />


                        {/* ARCHIVO */}

                        <Box>

                            <Typography
                                sx={{
                                    fontSize: "0.82rem",
                                    fontWeight: 700,
                                    color: "#334155",
                                    mb: 1,
                                }}
                            >
                                Archivo PDF
                            </Typography>


                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={
                                    <UploadFileRoundedIcon />
                                }
                                disabled={loading}
                                fullWidth
                                sx={{
                                    minHeight: 52,
                                    borderRadius: "11px",
                                    borderStyle: "dashed",
                                    textTransform: "none",
                                    justifyContent: "flex-start",
                                    px: 2,
                                    color: "#475569",
                                    borderColor: "#CBD5E1",

                                    "&:hover": {
                                        borderStyle: "dashed",
                                        borderColor:
                                            "#3157A4",
                                        backgroundColor:
                                            "#F8FAFC",
                                    },
                                }}
                            >

                                {formulario.archivo
                                    ? formulario.archivo.name
                                    : "Seleccionar archivo PDF"}

                                <input
                                    hidden
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    onChange={handleArchivo}
                                />

                            </Button>

                        </Box>

                    </Stack>

                </DialogContent>


                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.5,
                        gap: 1,
                    }}
                >

                    <Button
                        onClick={cerrarFormulario}
                        disabled={loading}
                        sx={{
                            borderRadius: "10px",
                            px: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#64748B",
                        }}
                    >
                        Cancelar
                    </Button>


                    <Button
                        variant="contained"
                        onClick={handleGuardar}
                        disabled={loading}
                        sx={{
                            borderRadius: "10px",
                            px: 2.5,
                            textTransform: "none",
                            fontWeight: 700,
                            backgroundColor: "#3157A4",

                            "&:hover": {
                                backgroundColor: "#264889",
                            },
                        }}
                    >

                        {loading ? (

                            <CircularProgress
                                size={21}
                                sx={{
                                    color: "#fff",
                                }}
                            />

                        ) : (
                            "Guardar documento"
                        )}

                    </Button>

                </DialogActions>

            </Dialog>


            {/* ===================================================== */}
            {/* STATUS */}
            {/* ===================================================== */}

            <StatusModal
                open={statusModal.open}
                type={statusModal.type}
                message={statusModal.message}
                onClose={cerrarStatus}
            />

        </Box>

    );
};